import _ from 'underscore';

import { BaseSchemaProcessor } from './base-schema-processor';
import { updateDeliveryStatusPropertyForFilter } from '../util/update-delivery-status-property-for-filter';
import { updateAuctionInsightPropertyForFilter } from '../util/update-auction-insight-property-for-filter';
import { initialize as initializeCampaignLanguages, updateEntitySchemaForCampaignLanguages, updateOdataOptionsForCampaignLanguages } from '../util/update-campaign-languages-property-for-filter';
import { initialize as initializeAvailableInsightTypeIds, updateEntitySchemaForAvailableInsightTypeIds, updateOdataOptionsForAvailableInsightTypeIds } from '../util/update-available-insight-type-ids-property-for-filter';
import { updateStatusPropertyForFilter } from '../util/update-status-property-for-filter';

export class CampaignSchemaProcessor extends BaseSchemaProcessor {
  /**
   * @param {Object} options - The options object that is used to configure schema processor
   * @param {Object} options.i18n - Legacy global i18n
   * @param {Object} options.schemaI18nCampaign - Campaign schema i18n
   * @param {Object} options.languageI18n - Multi language selector i18n
   * @param {Object} options.s$AdGroup - Adgroup schema
   * @param {Object} options.globalConfig - Global app config
   */
  constructor(options) {
    initializeCampaignLanguages(options);
    initializeAvailableInsightTypeIds(options);
    super(options);
    this.newI18n = options.newI18n;
    this.isFromCampaignGrid = options.isFromCampaignGrid;
  }

  updateEntitySchemaForFeatures(entitySchema) {
    const updatedSchema = super.updateEntitySchemaForFeatures(entitySchema);

    let deliveryStatusEnumSet = ['InvalidFinancialStatusOther',
      'CampaignSuspended',
      'CampaignBudgetPaused',
      'CampaignUserPaused',
      'Eligible',
      'BudgetSuggestions',
      'AutoBiddingLimitedByNotEnoughConversions',
      'AutoBiddingLearning',
      'NoEligibleAdGroups',
      'CampaignInReview',
      'CPSNonRetailDomain',
      'CPSInsufficientConversions',
      'CPSAttributionBroken',
    ];

    // should remove old pilot after new pilot enabled
    if (this.Permissions.IsCampaignTargetROASBidStrategyEnabled || this.Permissions.IsNewCampaignTargetROASBidStrategyEnabled) { // eslint-disable-line
      const idx = deliveryStatusEnumSet.indexOf('AutoBiddingLimitedByNotEnoughConversions');

      deliveryStatusEnumSet = [...deliveryStatusEnumSet.slice(0, idx), 'AutoBiddingLimitedByNotEnoughRevenue', ...deliveryStatusEnumSet.slice(idx)];
    }

    if (this.Permissions && this.Permissions.dynamic && this.Permissions.dynamic.AutoBiddingStrategies) {
      deliveryStatusEnumSet.splice(deliveryStatusEnumSet.indexOf('AutoBiddingLimitedByNotEnoughConversions') + 1, 0, 'AutoBiddingLimitedInventoryLimit', 'AutoBiddingLimitedMaxCPCRestriction', 'AutoBiddingLimitedBudgetRestriction', 'AutoBiddingLimitedUnAttainableTarget', 'AutoBiddingPortfolioBidStrategyTrainingStatus');
    }

    if (this.Permissions && this.Permissions.dynamic && this.Permissions.dynamic.LinkedInCampaigns) {
      deliveryStatusEnumSet = [...deliveryStatusEnumSet, 'AdGroupExpired', 'AudienceTooNarrow', 'LinkedInAccountServingHold', 'LinkedInCompanyPagePaidMediaRequired'];
    }

    if (this.Permissions && this.Permissions.dynamic && this.Permissions.dynamic.CampaignLifetimeBudget) {
      deliveryStatusEnumSet = [...deliveryStatusEnumSet, 'CampaignExpired'];
    }

    updateStatusPropertyForFilter(entitySchema);
    updateDeliveryStatusPropertyForFilter(updatedSchema, deliveryStatusEnumSet);

    updateEntitySchemaForCampaignLanguages(updatedSchema);
    updateAuctionInsightPropertyForFilter(updatedSchema);

    // update campaign title to 'campaign name', status title to 'Campaign Status'
    // for campaign grid filter
    const nameOption = _.find(updatedSchema.properties, item => item.name === 'Name');

    nameOption.title = this.i18n.getString('FilterLocalization_DataAttribute_CampaignName');

    const campaignStatusOption = _.find(updatedSchema.properties, item => item.name === 'Status');

    campaignStatusOption.title = this.i18n.getString('FilterLocalization_DataAttribute_CampaignStatus');

    // update title for seasonality adjustment id.
    // Spatial case, localize as "Seasonality adjustment name"
    const SeasonalityAdjustment = _.find(updatedSchema.properties, item => item.name === 'SeasonalityAdjustmentId');
    const DataExclusion = _.find(updatedSchema.properties, item => item.name === 'DataExclusionId');

    if (this.newI18n) {
      SeasonalityAdjustment.title = this.newI18n.getString(_TL_('Seasonality adjustment name'));
      DataExclusion.title = this.newI18n.getString(_TL_('Data exclusion name'));
    }

    // if there is SeasonalityAdjustment, there must be DataExclusion
    /* istanbul ignore next */
    if (SeasonalityAdjustment) {
      if (this.isFromCampaignGrid) {
        SeasonalityAdjustment.filterable = true;
        DataExclusion.filterable = true;
      } else {
        SeasonalityAdjustment.filterable = false;
        DataExclusion.filterable = false;
      }
    }

    // update title for campaign id
    const idOption = _.find(updatedSchema.properties, item => item.name === 'Id');

    idOption.title = this.i18n.getString('FilterLocalization_DataAttribute_CampaignId');

    if (this.isForVNext) {
      // These properties are not supported by google.
      // For first iteration, we will drop their support.
      delete updatedSchema.properties.Languages;

      updateEntitySchemaForAvailableInsightTypeIds(updatedSchema);
    }

    if (this.Permissions.dynamic && (this.Permissions.dynamic.XandrVideoCampaigns || this.Permissions.dynamic.XandrDisplayAdsCampaigns)) {
      const biddingStrategyType = updatedSchema.properties.DisplayBiddingStrategyType;
      if (!biddingStrategyType.localized) {
        biddingStrategyType.localized = {};
      }

      biddingStrategyType.localized["Enum.BiddingStrategyType'ManualCpm'"] = this.i18n.getString('BidStrategy_ManualCpm_ViewableCpm');

      if (this.Permissions.dynamic.XandrVideoCampaigns) {
        biddingStrategyType.localized["Enum.BiddingStrategyType'ManualCpv'"] = this.i18n.getString('BidStrategy_ManualCpv_Cpcv');
      }
    }

    updatedSchema.properties.DisplayBiddingStrategyTargetCpa = {
      filterable: true,
      type: 'number',
      property: 'DisplayBiddingStrategyTargetCpa',
    };
    updatedSchema.properties.DisplayBiddingStrategyTargetRoas = {
      filterable: true,
      type: 'number',
      maximum: 10000000,
      property: 'DisplayBiddingStrategyTargetRoas',
      isPercentValue: true,
    };
    updatedSchema.properties.DisplayBiddingStrategyTargetImpressionShare = {
      filterable: true,
      type: 'number',
      isPercentValue: true,
      property: 'DisplayBiddingStrategyTargetImpressionShare',
    };
    if (this.Permissions.dynamic.AppAdsCampaignPhaseOne) {
      updatedSchema.properties.DisplayBiddingStrategyManualCpi = {
        filterable: true,
        type: 'number',
        property: 'DisplayBiddingStrategyManualCpi',
      };
    }
    if (this.Permissions.dynamic.UnifiedAppAds) {
      updatedSchema.properties.DisplayBiddingStrategyManualCpc = {
        filterable: true,
        type: 'number',
        property: 'DisplayBiddingStrategyManualCpc',
      };
    }

    // automated rule will not work if wrap below in if (isVNext)
    if (this.Permissions.IsPersonalizedOffersEnabled) {
      updatedSchema.properties.CampaignCashbackPercent = {
        filterable: true,
        type: 'number',
        minimum: 0,
        maximum: 50,
        property: 'CampaignCashback/Percent',
        required: true,
      };
      if (!(this.Permissions.IsPersonalizedOffersV2Enabled ||
        this.Permissions.IsPersonalizedOffersV3Enabled)) {
        updatedSchema.properties.CampaignCashbackMonthlyBudget = {
          filterable: true,
          type: 'number',
          property: 'CampaignCashback/MonthlyBudget',
          required: true,
        };
      }
      updatedSchema.properties.CampaignCashbackScope = {
        filterable: true,
        type: 'string',
        property: 'CampaignCashback/Scope',
        chooseType: 'checkbox',
        operators: ['eq'],
        enum: ["Enum.CashbackScope'None'", "Enum.CashbackScope'Store'", "Enum.CashbackScope'Product'"],
        enumValueMap: {
          "Enum.CashbackScope'None'": 'None',
          "Enum.CashbackScope'Store'": 'Store',
          "Enum.CashbackScope'Product'": 'Product',
        },
        required: true,
      };
      if (this.Permissions.IsPersonalizedOffersV3Enabled) {
        updatedSchema.properties.Coupon = {
          filterable: true,
          type: 'string',
          property: 'CampaignCashback/CouponsEnabled',
          chooseType: 'checkbox',
          operators: ['in'],
          enum: ["Enum.CouponsEnabled'Enabled'", "Enum.CouponsEnabled'Disabled'"],
          enumValueMap: {
            "Enum.CouponsEnabled'Enabled'": 'Enabled',
            "Enum.CouponsEnabled'Disabled'": 'Disabled',
          },
          required: true,
        };
      }
    }
    if (!this.Permissions.IsOptimizationScoreInCampaignsGridEnabled) {
      delete updatedSchema.properties.OptimizationScore;
    }

    if ((!this.Permissions || !this.Permissions.dynamic || !this.Permissions.dynamic.LinkedInCampaigns)
      && updatedSchema.properties.CampaignType) {
      const campaignType = updatedSchema.properties.CampaignType;

      campaignType.enum = _.without(campaignType.enum, "Enum.CampaignType'LinkedIn'");
      campaignType.enumValueMap = _.omit(campaignType.enumValueMap, "Enum.CampaignType'LinkedIn'");
    }

    const isCampaignLifetimeBudgetEnabled = this.Permissions && this.Permissions.dynamic
      && this.Permissions.dynamic.CampaignLifetimeBudget
      && !this.Permissions.dynamic.MaeLifetimeBudgetExclusion;

    if (isCampaignLifetimeBudgetEnabled) {
      updatedSchema.properties.LifetimeBudget =
      {
        title: this.i18n.getString('FilterLocalization_DataAttribute_CampaignLifetimeBudgetAmount'),
        property: 'Budget/LifetimeBudgetAmount',
        type: 'number',
        minimum: 0,
        filterable: true,
      };

      updatedSchema.properties.BudgetAmount.title = this.i18n.getString('FilterLocalization_DataAttribute_CampaignDailyBudgetAmount');
    }

    return updatedSchema;
  }

  updateOdataOptions(odataOptions) {
    super.updateOdataOptions(odataOptions);
    updateOdataOptionsForCampaignLanguages(odataOptions);
    updateOdataOptionsForAvailableInsightTypeIds(odataOptions);

    _.extendOwn(odataOptions.custom, {
      DeliveryStatus: {
        eq: (value) => {
          let url = `DeliveryStatus eq ${value}`;

          if (value === "Enum.DeliveryStatus'Eligible'") {
            url += ' or DeliveryStatus eq Enum.DeliveryStatus\'InlineOpportunity\'';
          }

          return url;
        },
      },
      IsExperiment: {
        eq: (val) => {
          const operator = val ? 'gt' : 'le';

          return `ExperimentId ${operator} 0`;
        },
      },
      'CampaignCashback/Scope': {
        eq: (filterValues) => {
          const queryStr = _.map(filterValues, value => `CampaignCashback/Scope eq ${value}`).join(' or ');

          return `(${queryStr})`;
        },
      },
    });
    return odataOptions;
  }
}
